'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Button } from '@/components/Button';

dayjs.extend(utc);
dayjs.extend(timezone);

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface TimeRangeOption {
  label: string;
  value: string;
}

interface DateRangeFilterProps {
  onDateRangeChange?: (range: DateRange) => void;
  className?: string;
  initialRange?: DateRange;
  // Direction the dropdowns open. Default "down"; use "up" when the trigger
  // sits near the bottom of a container (e.g. the deliveries filter drawer).
  openDirection?: 'up' | 'down';
}

const timeRangeOptions: TimeRangeOption[] = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: 'last-7-days' },
  { label: 'Last 30 days', value: 'last-30-days' },
  { label: 'Last 3 Months', value: 'last-3-months' },
  { label: 'All Time', value: 'all-time' },
  { label: 'Custom', value: 'custom' },
];

const Calendar: React.FC<{
  range: DateRange;
  onRangeChange: (range: DateRange) => void;
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
  showDualCalendar?: boolean;
}> = ({ range, onRangeChange, currentMonth, onMonthChange }) => {
  // Parse the input's YYYY-MM-DD as local midnight. `new Date('YYYY-MM-DD')`
  // parses as UTC midnight, which lands on the previous day in timezones
  // behind UTC and gives a 07:00 WIB timestamp instead of 00:00 here.
  const parseInputDate = (value: string): Date | null => {
    if (!value) return null;
    const parts = value.split('-');
    if (parts[0].length > 4) parts[0] = parts[0].slice(-4);
    const [y, m, d] = parts.map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const handleStartDateChange = (value: string) => {
    onRangeChange({ from: parseInputDate(value), to: range.to });
  };

  const handleEndDateChange = (value: string) => {
    onRangeChange({ from: range.from, to: parseInputDate(value) });
  };

  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    return dayjs(date).format('YYYY-MM-DD');
  };

  const handlePrevMonth = () => {
    onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Month Navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-stroke-soft-200">
        <button onClick={handlePrevMonth} className="p-1 rounded-sm hover:bg-bg-weak-50">
          <i className="ri-arrow-left-s-line text-icon-sub-600"></i>
        </button>
        <span className="font-medium text-text-strong-950">
          {dayjs(currentMonth).format('MMMM YYYY')}
        </span>
        <button onClick={handleNextMonth} className="p-1 rounded-sm hover:bg-bg-weak-50">
          <i className="ri-arrow-right-s-line text-icon-sub-600"></i>
        </button>
      </div>

      {/* Date Range Inputs */}
      <div className="flex-1 p-3 space-y-3">
        <div>
          <label className="block text-xs font-medium text-text-sub-600 mb-1.5">Start Date</label>
          <input
            type="date"
            max="9999-12-31"
            value={formatDateForInput(range.from)}
            onChange={(e) => handleStartDateChange(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-stroke-sub-300 rounded-md bg-bg-white-0 text-text-strong-950 focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-text-sub-600 mb-1.5">End Date</label>
          <input
            type="date"
            max="9999-12-31"
            value={formatDateForInput(range.to)}
            onChange={(e) => handleEndDateChange(e.target.value)}
            min={formatDateForInput(range.from)}
            className="w-full px-3 py-1.5 text-sm border border-stroke-sub-300 rounded-md bg-bg-white-0 text-text-strong-950 focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        {/* Quick Calendar View */}
        <div className="mt-3">
          <div className="text-xs text-text-soft-400 mb-1.5">Current Month Calendar</div>
          <div className="grid grid-cols-7 gap-0.5 text-center text-xs">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div key={day} className="font-medium text-text-soft-400 p-1">{day}</div>
            ))}
            {/* Leading blanks so day 1 lands on its actual weekday column */}
            {Array.from({ length: dayjs(currentMonth).startOf('month').day() }, (_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: dayjs(currentMonth).daysInMonth() }, (_, i) => {
              const date = dayjs(currentMonth).date(i + 1).toDate();
              const isInRange = range.from && range.to && date >= range.from && date <= range.to;
              const isStart = range.from && dayjs(date).isSame(range.from, 'day');
              const isEnd = range.to && dayjs(date).isSame(range.to, 'day');

              return (
                <button
                  key={i}
                  onClick={() => {
                    if (!range.from || (range.from && range.to)) {
                      onRangeChange({ from: date, to: null });
                    } else {
                      if (date < range.from) {
                        onRangeChange({ from: date, to: range.from });
                      } else {
                        onRangeChange({ from: range.from, to: date });
                      }
                    }
                  }}
                  className={`
                    p-1 text-xs rounded
                    ${isStart || isEnd ? 'bg-primary text-static-white' : ''}
                    ${isInRange && !isStart && !isEnd ? 'bg-feature-lighter text-primary' : ''}
                    ${!isInRange && !isStart && !isEnd ? 'hover:bg-bg-weak-50 text-text-strong-950' : ''}
                  `}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  onDateRangeChange,
  className = '',
  initialRange = { from: null, to: null },
  openDirection = 'down',
}) => {
  const dropdownPos =
    openDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1';
  const [range, setRange] = useState<DateRange>(initialRange);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('all-time');
  const [isTimeRangeOpen, setIsTimeRangeOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const timeRangeRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (timeRangeRef.current && !timeRangeRef.current.contains(event.target as Node)) {
        setIsTimeRangeOpen(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsDatePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDateRangeForTimeOption = (option: string): DateRange => {
    const createLocalDate = (daysFromToday: number = 0): Date => {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysFromToday, 0, 0, 0, 0);
    };

    switch (option) {
      case 'today': return { from: createLocalDate(0), to: createLocalDate(0) };
      case 'last-7-days': return { from: createLocalDate(-7), to: createLocalDate(0) };
      case 'last-30-days': return { from: createLocalDate(-30), to: createLocalDate(0) };
      case 'last-3-months': return { from: dayjs().subtract(3, 'month').startOf('day').toDate(), to: createLocalDate(0) };
      case 'all-time': return { from: null, to: null };
      default: return { from: null, to: null };
    }
  };

  const handleTimeRangeSelect = (option: string) => {
    setSelectedTimeRange(option);
    setIsTimeRangeOpen(false);

    // Only "Custom" needs the calendar to manually pick start/end.
    if (option === 'custom') {
      setIsDatePickerOpen(true);
      return;
    }

    // Every preset (incl. "All Time") resolves to a concrete range and is
    // applied immediately — no extra popup/confirm step.
    const newRange = getDateRangeForTimeOption(option);
    setRange(newRange);
    setCurrentMonth(newRange.from ?? new Date());
    onDateRangeChange?.(newRange);
  };

  const handleDateRangeChange = useCallback((newRange: DateRange) => {
    setRange(newRange);
    if (newRange.from || newRange.to) setSelectedTimeRange('custom');
  }, []);

  const handleApplyDateRange = () => { setIsDatePickerOpen(false); onDateRangeChange?.(range); };

  const handleClearDateRange = () => {
    const clearedRange = { from: null, to: null };
    setRange(clearedRange);
    setSelectedTimeRange('all-time');
    onDateRangeChange?.(clearedRange);
  };

  const formatDateRange = (range: DateRange): string => {
    if (!range.from) return 'Custom Range';
    const from = dayjs(range.from);
    if (!range.to || from.isSame(range.to, 'day')) return from.format('D MMMM YYYY');
    return `${from.format('D MMMM YYYY')} - ${dayjs(range.to).format('D MMMM YYYY')}`;
  };

  const getSelectedTimeRangeLabel = () => {
    return timeRangeOptions.find((opt) => opt.value === selectedTimeRange)?.label || 'All Time';
  };

  return (
    <div className={`relative inline-flex ${className}`}>
      {/* Time Range Dropdown */}
      <div className="relative" ref={timeRangeRef}>
        <button
          onClick={() => setIsTimeRangeOpen(!isTimeRangeOpen)}
          className="relative focus:z-10 flex items-center gap-2 px-3 py-2 border border-stroke-sub-300 rounded-l-md bg-bg-white-0 hover:bg-bg-weak-50 focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary text-sm text-text-sub-600"
        >
          <span className="truncate max-w-32">{getSelectedTimeRangeLabel()}</span>
          <i className={`ri-arrow-down-s-line transition-transform ${isTimeRangeOpen ? 'rotate-180' : ''}`}></i>
        </button>

        {isTimeRangeOpen && (
          <div className={`absolute ${dropdownPos} z-50 w-48 bg-bg-white-0 border border-stroke-soft-200 rounded-md shadow-card-sm`}>
            <div className="p-1">
              {timeRangeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleTimeRangeSelect(option.value)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                    selectedTimeRange === option.value
                      ? 'bg-feature-lighter text-primary font-medium'
                      : 'text-text-sub-600 hover:bg-bg-weak-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Date Picker Button — panel anchors to the whole control's left edge
          (via the outer relative container) so it grows rightward and never
          spills out of a narrow container like the filter drawer. */}
      <div ref={datePickerRef}>
        <button
          onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
          className="relative focus:z-10 flex items-center gap-2 px-3 py-2 border border-l-0 border-stroke-sub-300 rounded-r-md bg-bg-white-0 hover:bg-bg-weak-50 focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary text-sm text-text-sub-600"
        >
          <i className="ri-calendar-line text-icon-sub-600"></i>
          <span className="truncate max-w-64">
            {formatDateRange(range)}
          </span>
        </button>

        {isDatePickerOpen && (
          <div
            className={`absolute left-0 ${dropdownPos} z-50 w-[300px] max-w-[calc(100vw-2rem)] bg-bg-white-0 border border-stroke-soft-200 rounded-lg shadow-card-md`}
          >
            <div className="flex flex-col h-full rounded-lg overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-stroke-soft-200 bg-bg-weak-50">
                <h3 className="text-sm font-medium text-text-strong-950">Select Date Range</h3>
                <button
                  onClick={() => setIsDatePickerOpen(false)}
                  className="text-icon-soft-400 hover:text-text-sub-600"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {/* Calendar */}
              <Calendar
                range={range}
                onRangeChange={handleDateRangeChange}
                currentMonth={currentMonth}
                onMonthChange={setCurrentMonth}
              />

              {/* Footer */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-stroke-soft-200 bg-bg-weak-50">
                <p className="text-sm text-text-sub-600">
                  {range.from && range.to
                    ? `${dayjs(range.from).format('MMM DD, YYYY')} - ${dayjs(range.to).format('MMM DD, YYYY')}`
                    : range.from
                      ? dayjs(range.from).format('MMM DD, YYYY')
                      : 'No range selected'}
                </p>
                <div className="flex gap-2">
                  <Button size="small" variant="neutral" onClick={handleClearDateRange}>Clear</Button>
                  <Button size="small" variant="primary" onClick={handleApplyDateRange} disabled={!range.from}>Apply</Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangeFilter;
